"use client";

import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { FilterBar, ProductGrid, useProductFilters } from "@/ui/components/plp";
import {
	getLocalizedPriceRanges,
	type CategoryOption,
	type ProductOrPeptideCard,
} from "@/ui/components/plp/filter-utils";
import type { ResidualFacets } from "@/lib/catalog/filtering";
import { NumberedPagination } from "@/ui/components/numbered-pagination";
import { buttonStyles } from "@/ui/components/ui/button";

export interface ListingPagination {
	page: number;
	totalPages: number;
	totalCount: number;
	rangeStart: number;
	rangeEnd: number;
}

interface ProductsPageClientProps {
	/** Current page slice of the FULLY filtered result set. */
	products: ProductOrPeptideCard[];
	pagination: ListingPagination;
	/** Facet options with globally correct counts (server-computed). */
	facets: ResidualFacets;
	categoryOptions: CategoryOption[];
	/** Categories resolved from URL slugs (server-side) for active filter display */
	resolvedCategories?: Array<{ slug: string; id: string; name: string }>;
}

export function ProductsPageClient({
	products,
	pagination,
	facets,
	categoryOptions,
	resolvedCategories = [],
}: ProductsPageClientProps) {
	const t = useTranslations("plp");
	const { channel = "eu" } = useParams<{ channel: string }>();
	const {
		selectedCategories,
		selectedColors,
		selectedSizes,
		selectedPriceRange,
		searchQuery,
		selectedPeptideCategories,
		selectedResearchLevel,
		selectedAvailability,
		sortValue,
		activeFilters,
		handleCategoryToggle,
		handleColorToggle,
		handleSizeToggle,
		handlePriceRangeChange,
		handleSearchChange,
		handlePeptideCategoryToggle,
		handleResearchLevelChange,
		handleAvailabilityChange,
		handleSortChange,
		handleRemoveFilter,
		handleClearFilters,
	} = useProductFilters({
		resolvedCategories,
		enableCategoryFilter: true,
	});

	return (
		<FilterBar
			resultCount={pagination.totalCount}
			resultRange={
				pagination.totalPages > 1 ? { start: pagination.rangeStart, end: pagination.rangeEnd } : null
			}
			sortValue={sortValue}
			onSortChange={handleSortChange}
			categoryOptions={categoryOptions}
			colorOptions={facets.colorOptions}
			sizeOptions={facets.sizeOptions}
			priceRanges={getLocalizedPriceRanges(channel, t)}
			peptideCategoryOptions={facets.peptideCategoryOptions}
			researchLevelOptions={facets.researchLevelOptions}
			availabilityOptions={facets.availabilityOptions}
			selectedCategories={selectedCategories}
			selectedColors={selectedColors}
			selectedSizes={selectedSizes}
			selectedPriceRange={selectedPriceRange}
			searchQuery={searchQuery}
			selectedPeptideCategories={selectedPeptideCategories}
			selectedResearchLevel={selectedResearchLevel}
			selectedAvailability={selectedAvailability}
			onCategoryToggle={handleCategoryToggle}
			onColorToggle={handleColorToggle}
			onSizeToggle={handleSizeToggle}
			onPriceRangeChange={handlePriceRangeChange}
			onSearchChange={handleSearchChange}
			onPeptideCategoryToggle={handlePeptideCategoryToggle}
			onResearchLevelChange={handleResearchLevelChange}
			onAvailabilityChange={handleAvailabilityChange}
			activeFilters={activeFilters}
			onRemoveFilter={handleRemoveFilter}
			onClearFilters={handleClearFilters}
		>
			{products.length > 0 ? (
				<ProductGrid products={products} />
			) : (
				<div className="rounded-xl border border-border bg-card py-12 text-center">
					<p className="text-lg text-muted-foreground">{t("noResultsMatch")}</p>
					<button
						onClick={handleClearFilters}
						className={buttonStyles({
							variant: "outline-solid",
							size: "sm",
							className: "mt-4 h-9",
						})}
					>
						{t("clearFilters")}
					</button>
				</div>
			)}
			<NumberedPagination page={pagination.page} totalPages={pagination.totalPages} />
		</FilterBar>
	);
}
